import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";
import { FEEDBACK_TAGS } from "./constants";
import { ModalShell, Field } from "../_dialog";

export default function FeedbackView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/founder/feedback");
      setItems(r.data?.feedback || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const remove = async (it) => {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await api.delete(`/founder/feedback/${it.id}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "workflow_friction")
      return items.filter((i) => (i.tags || []).includes("workflow_friction"));
    return items.filter((i) => i.status === filter);
  }, [items, filter]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="founder-os-feedback">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1 text-xs">
          {[
            ["all", "All"],
            ["workflow_friction", "Workflow friction"],
            ["new", "New"],
            ["reviewing", "Reviewing"],
            ["planned", "Planned"],
            ["resolved", "Resolved"],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setFilter(id)}
              data-testid={`feedback-filter-${id}`}
              className={`rounded-full px-2.5 py-1 font-semibold transition-colors ${
                filter === id
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="feedback-create-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800"
        >
          <Plus className="w-3.5 h-3.5" /> New feedback
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {filtered.map((f) => (
          <div
            key={f.id}
            className="flex items-start gap-3 p-3"
            data-testid={`feedback-row-${f.id}`}
          >
            <span
              className={`inline-flex items-center justify-center w-2 h-2 rounded-full mt-2 ${
                f.priority === "high"
                  ? "bg-rose-500"
                  : f.priority === "medium"
                  ? "bg-amber-500"
                  : "bg-slate-400"
              }`}
            />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate">{f.title}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                <span className="text-[10px] uppercase tracking-wider rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5 font-semibold">
                  {f.status}
                </span>
                {(f.tags || []).map((t) => (
                  <span
                    key={t}
                    className={`text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 font-semibold ${
                      t === "workflow_friction"
                        ? "bg-rose-100 text-rose-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {t.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
              {f.source && (
                <div className="text-[11px] text-slate-500 mt-1">Source: {f.source}</div>
              )}
              {f.notes && (
                <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{f.notes}</div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setEditing(f)}
                data-testid={`feedback-edit-${f.id}`}
                className="text-xs text-slate-500 hover:text-slate-900 px-2 py-1"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => remove(f)}
                data-testid={`feedback-delete-${f.id}`}
                className="text-rose-500 hover:bg-rose-50 rounded p-1"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-xs text-slate-500 p-6 text-center">No feedback in this view.</div>
        )}
      </div>

      {creating && (
        <FeedbackDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <FeedbackDialog
          record={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function FeedbackDialog({ record, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: record?.title || "",
    source: record?.source || "",
    notes: record?.notes || "",
    priority: record?.priority || "medium",
    status: record?.status || "new",
    tags: record?.tags || [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) =>
    set("tags", form.tags.includes(t) ? form.tags.filter((x) => x !== t) : [...form.tags, t]);

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      if (record) {
        await api.patch(`/founder/feedback/${record.id}`, form);
        toast.success("Feedback updated.");
      } else {
        await api.post("/founder/feedback", form);
        toast.success("Feedback added.");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={record ? "Edit feedback" : "Add feedback"}
      eyebrow="Feedback tracker"
      onClose={onClose}
      testId="feedback-dialog"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
            data-testid="feedback-dialog-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="feedback-dialog-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <Field label="Title">
        <input
          type="text"
          value={form.title}
          onChange={(e) => set("title", e.target.value)}
          autoFocus
          data-testid="feedback-dialog-title"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Priority">
          <select
            value={form.priority}
            onChange={(e) => set("priority", e.target.value)}
            data-testid="feedback-dialog-priority"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </Field>
        <Field label="Status">
          <select
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
            data-testid="feedback-dialog-status"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="new">New</option>
            <option value="reviewing">Reviewing</option>
            <option value="planned">Planned</option>
            <option value="resolved">Resolved</option>
          </select>
        </Field>
      </div>
      <Field label="Source">
        <input
          type="text"
          value={form.source}
          onChange={(e) => set("source", e.target.value)}
          placeholder="Who said this? Clinic name / vendor / internal."
          data-testid="feedback-dialog-source"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <Field label="Tags">
        <div className="flex flex-wrap gap-1.5">
          {FEEDBACK_TAGS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => toggleTag(t)}
              data-testid={`feedback-tag-${t}`}
              className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-1 font-semibold border transition-colors ${
                form.tags.includes(t)
                  ? t === "workflow_friction"
                    ? "bg-rose-600 border-rose-600 text-white"
                    : "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              {t.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Notes">
        <textarea
          rows={4}
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          data-testid="feedback-dialog-notes"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
    </ModalShell>
  );
}

// ===========================================================================
// Calendar
